import React, { useState, useEffect } from "react";
import { FiLogOut, FiPlus, FiRefreshCw } from "react-icons/fi";
import { FaCar, FaWalking, FaCalendarAlt } from "react-icons/fa";
import { AddEntryExitModal } from "./AddEntryExitModal";
import CommunityGateConfig from "./CommunityGateConfig";
import Modal from "./Modal";
import { API_BASE } from '../config'
import { formatLocalizedTime, localDateString, toLocalDateString } from '../utils/dateUtils'
import { buildVehicleLogExitPayload } from '../utils/vehicleLogUtils'

export default function EntryExitLog({ communityId: propCommunityId, trialMaxPreApproveDay, isTrial }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isGateManagerOpen, setIsGateManagerOpen] = useState(false);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dateFilter, setDateFilter] = useState(localDateString());
  const [exitingId, setExitingId] = useState(null);

  // Resolve communityId: prefer prop (set after community selection), fallback to sessionStorage
  const communityId = propCommunityId || sessionStorage.getItem('community_id') || '';

  const fetchLogs = async () => {
    setLoading(true);
    setError('');
    try {
      const idToken = sessionStorage.getItem('id_token');
      if (!idToken) { setError('Not authenticated'); setLoading(false); return; }

      const url = `${API_BASE}/vehicle-log?communityId=${encodeURIComponent(communityId)}`;

      const resp = await fetch(url, { headers: { Authorization: `Bearer ${idToken}` } });
      if (!resp.ok) { setError('Failed to load records'); setLoading(false); return; }

      const data = await resp.json();
      const mapped = (data || []).map((r) => ({
        id: `${r.COMMUNITY_ID || ''}_${r.ENTRY_TIME || crypto.randomUUID()}`,
        mode: r.MODE || (r.VEHICLE_NUMBER ? 'VEHICLE' : 'WALK'),
        personName: r.PERSON_NAME || r.OWNER_NAME || r.ownerName || '',
        personId: r.PERSON_ID || '',
        vehicleNumber: r.VEHICLE_NUMBER || r.vehicleNumber || '',
        unit: r.UNIT_NBR || r.unit || '',
        gate: r.GATE || r.gate || '',
        notes: r.NOTES || '',
        entryTime: r.ENTRY_TIME || r.entryTime || '',
        exitTime: r.EXIT_TIME || r.exitTime || '',
        status: (r.STATUS || 'INSIDE').toUpperCase(),
        raw: r,
      }));

      // Sort: INSIDE first, then by entry time descending
      mapped.sort((a, b) => {
        if (a.status === 'INSIDE' && b.status !== 'INSIDE') return -1;
        if (a.status !== 'INSIDE' && b.status === 'INSIDE') return 1;
        return new Date(b.entryTime) - new Date(a.entryTime);
      });

      setRecords(mapped);
    } catch (err) {
      console.error('fetch logs failed', err);
      setError('Failed to load records');
    }
    setLoading(false);
  };

  useEffect(() => { fetchLogs(); }, [dateFilter]);

  const handleMarkExit = async (record) => {
    setExitingId(record.id);
    try {
      const idToken = sessionStorage.getItem('id_token');
      if (!idToken) return;

      const payload = buildVehicleLogExitPayload({
        communityId: sessionStorage.getItem('community_id') || communityId,
        record,
      });

      const resp = await fetch(`${API_BASE}/vehicle-log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify(payload),
      });

      if (resp.ok) {
        fetchLogs();
      } else {
        const text = await resp.text();
        alert(`Exit failed: ${text}`);
      }
    } catch (err) {
      console.error('mark exit failed', err);
      alert('Failed to mark exit');
    }
    setExitingId(null);
  };

  const formatTime = (ts) => formatLocalizedTime(ts);

  const visibleRecords = records.filter((record) => {
    if (!dateFilter) return true
    const recordDate = toLocalDateString(record.entryTime || record.exitTime || '')
    return recordDate === dateFilter
  })

  const insideCount = visibleRecords.filter(r => r.status === 'INSIDE').length;
  const exitedCount = visibleRecords.filter(r => r.status === 'EXITED').length;

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h2 className="text-lg font-bold">Entry / Exit Log</h2>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <button
            onClick={fetchLogs}
            disabled={loading}
            className="p-2 text-gray-500 rounded-lg bg-gray-100 active:bg-gray-200"
          >
            <FiRefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => setIsGateManagerOpen(true)}
            className="hidden sm:inline-flex items-center justify-center gap-1.5 bg-white text-gray-700 border border-gray-200 px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 active:bg-gray-100"
          >
            Manage Entry/Exit Gates
          </button>
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center justify-center gap-1.5 bg-blue-600 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 flex-1 sm:flex-none"
          >
            <FiPlus size={16} />
            Log Entry
          </button>
        </div>
      </div>

      {/* Trial pre-approval limit banner */}
      {isTrial !== false && trialMaxPreApproveDay != null && trialMaxPreApproveDay >= 0 && (
        <div style={{
          background: '#fffbeb',
          border: '1px solid #fbbf24',
          borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 8,
          color: '#92400e'
        }}>
          {`Trial: up to ${trialMaxPreApproveDay} visitor pre-approvals/day. Upgrade for unlimited access.`}
        </div>
      )}

      {/* Date Filter */}
      <div className="relative mb-3">
        <FaCalendarAlt size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        <input
          type="date"
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          className="w-full block box-border border border-gray-200 rounded-xl pl-9 pr-3 py-3 text-sm bg-white shadow-sm appearance-none"
          style={{ WebkitAppearance: 'none', minWidth: 0 }}
        />
      </div>

      {/* Stats Bar */}
      {!loading && !error && records.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-green-700">{insideCount}</div>
            <div className="text-xs text-green-600">Inside</div>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-orange-700">{exitedCount}</div>
            <div className="text-xs text-orange-600">Exited</div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-blue-700">{visibleRecords.length}</div>
            <div className="text-xs text-blue-600">Total</div>
          </div>
        </div>
      )}

      {/* Records List */}
      <div className="space-y-3">
        {loading && (
          <div className="text-center py-8 text-gray-500 text-sm">Loading…</div>
        )}
        {error && (
          <div className="text-center py-8 text-red-500 text-sm">{error}</div>
        )}
        {!loading && !error && visibleRecords.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <p className="text-sm text-white font-medium">No Entry / Exit Details for this date</p>
          </div>
        )}

        {!loading && !error && visibleRecords.map((record) => (
          <div
            key={record.id}
            className={`bg-white border rounded-lg p-4 ${
              record.status === 'INSIDE'
                ? 'border-green-300 border-l-4 border-l-green-500'
                : 'border-gray-200'
            }`}
          >
            <div className="flex items-start gap-3">
              {/* Mode Icon */}
              <div className={`p-2 rounded-lg flex-shrink-0 ${
                record.status === 'INSIDE'
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-500'
              }`}>
                {record.mode === 'VEHICLE' ? <FaCar size={20} /> : <FaWalking size={20} />}
              </div>

              <div className="flex-1 min-w-0">
                {/* Name + Status Badge */}
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-medium text-gray-900 truncate">
                    {record.personName || 'Unknown'}
                  </h3>
                  <span className={`flex-shrink-0 px-2 py-0.5 rounded text-xs font-medium ${
                    record.status === 'INSIDE'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-orange-100 text-orange-800'
                  }`}>
                    {record.status}
                  </span>
                </div>

                {/* Unit + Gate */}
                <div className="text-sm text-gray-500 mt-0.5">
                  {record.unit && <span>Unit {record.unit}</span>}
                  {record.unit && record.gate && <span> • </span>}
                  {record.gate && <span>{record.gate}</span>}
                </div>

                {/* Vehicle number for VEHICLE mode */}
                {record.vehicleNumber && (
                  <div className="flex items-center gap-1.5 text-sm text-gray-700 mt-1">
                    <FaCar size={12} className="text-gray-400" />
                    <span className="font-mono text-sm">{record.vehicleNumber}</span>
                  </div>
                )}

                {/* Notes */}
                {record.notes && (
                  <p className="text-xs text-gray-500 mt-1 italic">📝 {record.notes}</p>
                )}

                {/* Entry / Exit Times */}
                <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                  <span>
                    <span className="text-green-600 font-medium">IN</span>{' '}
                    {formatTime(record.entryTime)}
                  </span>
                  {record.exitTime && (
                    <span>
                      <span className="text-orange-600 font-medium">OUT</span>{' '}
                      {formatTime(record.exitTime)}
                    </span>
                  )}
                </div>

                {/* Mark Exit Button */}
                {record.status === 'INSIDE' && (
                  <button
                    onClick={() => handleMarkExit(record)}
                    disabled={exitingId === record.id}
                    className="mt-2 flex items-center justify-center gap-1.5 px-3 py-2 bg-orange-500 text-white rounded-lg text-xs font-medium active:bg-orange-600 disabled:opacity-50 w-full sm:w-auto"
                  >
                    <FiLogOut size={14} />
                    {exitingId === record.id ? 'Marking…' : 'Mark Exit'}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add Entry Modal */}
      <AddEntryExitModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSaved={() => {
          setIsModalOpen(false);
          fetchLogs();
        }}
        communityId={sessionStorage.getItem('community_id') || ''}
      />
      {isGateManagerOpen && (
        <Modal title="Manage Entry/Exit Gates" onClose={() => setIsGateManagerOpen(false)}>
          <CommunityGateConfig communityId={communityId} />
        </Modal>
      )}
    </div>
  );
}
